import { useState } from "react";
import { Check, ChevronDown, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { Pill, PillKind } from "../ui/pill";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "../ui/dropdown-menu";

export interface ChipSelectOption {
  value: string;
  label: string;
}

/**
 * A badge that opens a dropdown of the other values when clicked; picking one
 * calls `onSelect` straight away. Meant for table cells inside a clickable row:
 * clicks and keys (including those from the portalled menu, which bubble through
 * the React tree) are stopped here so they don't also open the row.
 */
export function ChipSelect({
  value,
  options,
  label,
  pillKind,
  onSelect,
}: {
  value: string;
  options: ChipSelectOption[];
  /** Accessible name of the field, e.g. "Status". */
  label: string;
  pillKind: (value: string) => PillKind;
  onSelect: (value: string) => Promise<unknown>;
}) {
  const [busy, setBusy] = useState(false);
  const current = options.find(o => o.value === value)?.label ?? value;

  async function select(next: string) {
    if (next === value) return;
    setBusy(true);
    try {
      await onSelect(next);
    } finally {
      setBusy(false);
    }
  }

  return (
    <span className="inline-flex" onClick={e => e.stopPropagation()} onKeyDown={e => e.stopPropagation()}>
      <DropdownMenu>
        <DropdownMenuTrigger
          disabled={busy}
          aria-label={`${label}: ${current} — wijzigen`}
          className="group rounded-pill outline-none focus-visible:ring-2 focus-visible:ring-waves-accent disabled:cursor-wait"
        >
          <Pill kind={pillKind(value)} className="gap-1 pr-2 transition-[filter] group-hover:brightness-95">
            {current}
            {busy
              ? <Loader2 size={12} className="animate-spin" />
              : <ChevronDown size={12} className="opacity-60 group-hover:opacity-100" />}
          </Pill>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          {options.map(o => (
            <DropdownMenuItem key={o.value} onSelect={() => select(o.value)} className="justify-between gap-4">
              <Pill kind={pillKind(o.value)}>{o.label}</Pill>
              <Check className={cn("text-waves-ink", o.value !== value && "invisible")} />
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </span>
  );
}
